/**
 * บอสโลก round 2: the mechanics of the three bosses beyond the plain shapes (docs/systems/WORLD_BOSSES.md section 2). The fight
 * (systems/worldBosses.ts) owns the schedule, the phases and the strike; this holds the state of each trick and answers the
 * fight's questions: may it use this move, does this victim avoid it, how much of a hit counts, what does the HUD show.
 *
 * Ape: the ledge (a perch with a break window), mimic (a stance that beats what the attackers used), boulders as cover,
 * the small apes. Bird: aloft in phase 2 (only a high or ranged hit counts), the dive that lands it, fire and smoke on the
 * ground, eggs, shelters. Turtle: the shell with its rods and the crack, thunder doubled in water, bands of rings, safe
 * rocks, the wave toward the harbour, the storm.
 *
 * No DOM, no timers, no `Math.random`: draws come from the host, time from `dt`.
 */
import {
  WB_AIR,
  WB_COVER,
  WB_EGG,
  WB_HARBOUR,
  WB_INERT,
  WB_MIMIC,
  WB_MOVE_MECH,
  WB_PATCH,
  WB_PERCH,
  WB_SHELL,
  WB_SITES,
  WB_STORM,
  WB_THERMAL,
  WB_YOUNG,
  sitesOf,
  type MoveMech,
} from '../data/worldBossMech';
import { WB_SIM, type WorldBossDef } from '../data/worldBosses';
import { getSkill } from '../data/skills';
import { floorAt, PX_METRES } from '../map';
import type { WeatherKind } from '../data/weather';
import { inWater, isWaterAt } from '../qinggong';
import { MOVEMENT, type Entity, type World } from '../world';
import type { BossShape } from './bossMoves';
import type { SystemHost } from './types';

/** A fixed ground telegraph: `delay` seconds after the wind-up ends it strikes, only beyond `inner` metres (a band). */
export interface StrikeTele {
  readonly id: number;
  readonly shape: BossShape;
  readonly inner?: number;
  readonly delay?: number;
}

/** What a fight gives the mechanics. */
export interface MechCtx {
  readonly h: SystemHost;
  readonly def: WorldBossDef;
  readonly bossId: number;
  readonly mode: 'npc' | 'pvp';
  phase(): 1 | 2 | 3;
  humanIn(): boolean;
  /** Pays a mechanic point to a player. */
  credit(playerId: number): void;
  /** Strikes the victims in these telegraphs (a deferred band of rings). */
  strike(e: Entity, move: string, teles: StrikeTele[]): void;
}

interface Site {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  readonly z: number;
}
interface Patch {
  readonly kind: 'fire' | 'smoke';
  readonly move: string;
  readonly x: number;
  readonly y: number;
  readonly r: number;
  t: number;
  acc: number;
}
interface Cover {
  readonly x: number;
  readonly y: number;
  t: number;
}
interface Pending {
  t: number;
  readonly move: string;
  readonly tele: StrikeTele;
}
interface Egg {
  readonly id: number;
  readonly x: number;
  readonly y: number;
  age: number;
  simmed: boolean;
}

/** What the renderer and HUD read of the mechanics. */
export interface MechView {
  /** The ape on its ledge: where, how high, the breaks landed and needed, seconds left. */
  readonly perch: { readonly x: number; readonly y: number; readonly z: number; readonly breaks: number; readonly need: number; readonly left: number } | null;
  /** The glyph over the ape during the mimic wind-up (攻 or 破: the kind it will beat), and the stance it holds. */
  readonly mimicGlyph: 'attack' | 'break' | null;
  readonly stance: { readonly kind: 'guard' | 'palms'; readonly counters: 'attack' | 'break'; readonly left: number } | null;
  /** The bird aloft (hits need height or range), its altitude in metres, and the seconds it lies stunned on the ground. */
  readonly aloft: boolean;
  readonly altitude: number;
  readonly downFor: number;
  readonly eggs: readonly { readonly id: number; readonly x: number; readonly y: number; readonly left: number }[];
  /** The shell closed: seconds left and rods standing; and cracked (damage x1.25) after the rods fall. */
  readonly shell: { readonly left: number; readonly rods: number; readonly total: number } | null;
  readonly cracked: boolean;
  readonly storm: boolean;
  readonly swell: number;
  /** Marked safe rocks while a marked move winds up. */
  readonly marks: readonly Site[];
  /** Thermal columns a glider rides up to the bird (phase 2). */
  readonly thermals: readonly Site[];
  readonly patches: readonly { readonly kind: 'fire' | 'smoke'; readonly x: number; readonly y: number; readonly r: number; readonly left: number }[];
  readonly covers: readonly { readonly x: number; readonly y: number; readonly r: number; readonly left: number }[];
  readonly adds: number;
}

const COVER_MAX = WB_COVER.max;
const PATCH_MAX = 60;

export class BossMech {
  private clock = 0;
  private patches: Patch[] = [];
  private covers: Cover[] = [];
  private pending: Pending[] = [];
  private perch: { t: number; volleyT: number; simT: number; breaks: number; by: Set<number> } | null = null;
  private hist: { t: number; kind: 'attack' | 'break' }[] = [];
  private glyph: 'attack' | 'break' | null = null;
  private stance: { kind: 'guard' | 'palms'; counters: 'attack' | 'break'; t: number } | null = null;
  private aloft = false;
  private downT = 0;
  private eggs: Egg[] = [];
  private eggGap = 0;
  private shell: { t: number; rods: number[]; simT: number } | null = null;
  private cracked = false;
  private storm = false;
  private weatherWas: WeatherKind | null = null;
  private marks: Site[] = [];
  private adds: number[] = [];
  private lastHit = new Map<number, number>();

  constructor(private readonly c: MechCtx) {}

  private get w(): World {
    return this.c.h.world;
  }

  // ───────────────────────── sites ─────────────────────────

  /** A site by id, in map coordinates. */
  site(id: string): Site {
    const s = WB_SITES[id]!;
    const a = this.c.def.arena;
    return { id, x: a.x + s.dx, y: a.y + s.dy, r: s.r, z: s.z ?? 0 };
  }

  private sites(prefixes: readonly string[]): Site[] {
    return prefixes.flatMap((p) => sitesOf(p)).map((id) => this.site(id));
  }

  private onSite(v: { x: number; y: number }, prefixes: readonly string[]): boolean {
    return this.sites(prefixes).some((s) => Math.hypot(v.x - s.x, v.y - s.y) <= s.r);
  }

  // ───────────────────────── the fight's questions ─────────────────────────

  /** The fight is running a script of its own (the perch): the dice stay off. */
  busy(): boolean {
    return !!this.perch;
  }

  /** May the dice pick this move now. */
  canPick(move: string): boolean {
    const mm = WB_MOVE_MECH[move];
    if (!mm) return true;
    if (mm.scheduled) return false;
    if (mm.groundOnly && this.aloft) return false;
    return true;
  }

  /** A fighter standing in water (not on a rock or the deck, not skimming, not in the air). */
  wet(v: Entity): boolean {
    if (v.z > MOVEMENT.airDodgeHeight) return false;
    if (this.onSite(v, ['wb-turtle:rock', 'wb-turtle:deck'])) return false;
    const w = this.w;
    return v === w.player ? inWater(w.qinggong) : isWaterAt(w.map, v.x, v.y);
  }

  private skimming(v: Entity): boolean {
    const w = this.w;
    return v === w.player && w.qinggong.mode === 'skim';
  }

  /** A boulder between the boss and the victim, close enough behind it. */
  private covered(e: Entity, v: Entity): boolean {
    for (const c of this.covers) {
      const dx = v.x - e.x;
      const dy = v.y - e.y;
      const l2 = dx * dx + dy * dy || 1;
      const t = ((c.x - e.x) * dx + (c.y - e.y) * dy) / l2;
      if (t <= 0 || t >= 1) continue;
      const px = e.x + dx * t;
      const py = e.y + dy * t;
      if (Math.hypot(c.x - px, c.y - py) > WB_COVER.r) continue;
      if (Math.hypot(v.x - c.x, v.y - c.y) <= WB_COVER.r + WB_COVER.behind) return true;
    }
    return false;
  }

  /** Whether a victim avoids the move (skip) and how much of its power and stun it takes. */
  shield(v: Entity, e: Entity, move: string): { skip: boolean; pm: number; sm: number } {
    const mm = WB_MOVE_MECH[move];
    const out = { skip: false, pm: 1, sm: 1 };
    if (!mm) return out;
    if (mm.cover && this.covered(e, v)) out.skip = true;
    if (mm.safe && this.onSite(v, mm.safe.sites)) out.skip = true;
    if (mm.harbour && this.skimming(v)) out.skip = true;
    if (mm.thunder && this.wet(v)) {
      out.pm = 2;
      out.sm = 2;
    }
    if (mm.halfIfGuardFull && v.blocking && v.stamina >= v.maxStamina * 0.98) out.pm *= 0.5;
    return out;
  }

  /** Where a knockback from this move goes: away from the boss, or toward the harbour. */
  pushDir(move: string): { dx: number; dy: number } | null {
    return WB_MOVE_MECH[move]?.harbour ? WB_HARBOUR : null;
  }

  // ───────────────────────── casting ─────────────────────────

  /** The bands of a ring move: one disc per outer radius, each striking `gap` seconds after the last. */
  bands(move: string, base: BossShape): StrikeTele[] | null {
    const mm = WB_MOVE_MECH[move];
    if (!mm?.rings || base.k !== 'ring') return null;
    const gap = mm.gap ?? 0.6;
    return mm.rings.map((r, i) => ({
      id: 0,
      shape: { k: 'ring', x: base.x, y: base.y, r },
      inner: i ? mm.rings![i - 1]! : 0,
      delay: i * gap,
    }));
  }

  /** A wind-up begins: the mimic reads the attackers, a marked move shows its safe rocks. */
  onCast(e: Entity, move: string): void {
    const mm = WB_MOVE_MECH[move];
    if (mm?.script === 'mimic') {
      const recent = this.hist.filter((x) => this.clock - x.t <= WB_MIMIC.window);
      const breaks = recent.filter((x) => x.kind === 'break').length;
      this.glyph = breaks > recent.length - breaks ? 'break' : 'attack';
    }
    if (mm?.safe?.marked) this.marks = this.sites(mm.safe.sites);
    void e;
  }

  /** A cast ended without striking (broken, phase change, leash): marks and glyph go, deferred bands are called off. */
  onCancel(): TeleEndIds {
    this.glyph = null;
    this.marks = [];
    const ids = this.pending.map((p) => p.tele.id);
    this.pending = [];
    return ids;
  }

  /** A wind-up reaches its end. Scripts run here; false when the move strikes the ordinary way afterwards. */
  script(e: Entity, move: string): void {
    const mm = WB_MOVE_MECH[move];
    this.marks = [];
    if (mm?.script === 'mimic') {
      const counters = this.glyph ?? 'attack';
      this.stance = { kind: counters === 'attack' ? 'guard' : 'palms', counters, t: WB_MIMIC.stance };
      this.glyph = null;
      this.c.h.notice(counters === 'attack' ? `${this.c.def.name}กอดอกตั้งรับ` : `${this.c.def.name}แบมือรอรับ`);
    } else if (mm?.script === 'young') {
      this.callYoung(e);
    } else if (mm?.script === 'eggs') {
      this.layEggs();
    }
  }

  /** Holds the bands that follow the first. */
  defer(move: string, teles: StrikeTele[]): void {
    for (const tele of teles) this.pending.push({ t: tele.delay ?? 0, move, tele });
  }

  /** After a strike: what the move leaves on the ground, and a dive's landing. */
  after(e: Entity, move: string, teles: StrikeTele[]): void {
    const mm = WB_MOVE_MECH[move];
    if (!mm) return;
    if (mm.leave) for (const t of teles) this.leave(mm.leave, move, t.shape);
    if (mm.landStun && this.aloft) {
      this.aloft = false;
      this.downT = mm.landStun;
      e.stunT = Math.max(e.stunT, mm.landStun);
      this.c.h.notice(`${this.c.def.name}ร่วงลงพื้น!`);
    }
  }

  private leave(l: NonNullable<MoveMech['leave']>, move: string, s: BossShape): void {
    if (l.kind === 'boulder') {
      if (s.k !== 'ring') return;
      this.covers.push({ x: s.x, y: s.y, t: l.seconds });
      while (this.covers.length > COVER_MAX) this.covers.shift();
      return;
    }
    const add = (x: number, y: number, r: number) => {
      if (this.patches.length < PATCH_MAX) this.patches.push({ kind: l.kind as 'fire' | 'smoke', move, x, y, r, t: l.seconds, acc: 0 });
    };
    if (s.k === 'ring') add(s.x, s.y, s.r);
    else if (s.k === 'cone') for (let i = 1; i <= 3; i++) add(s.x + (s.dx * s.r * i) / 3, s.y + (s.dy * s.r * i) / 3, s.r / 3);
    else if (s.k === 'line') for (let d = s.half; d <= s.len; d += s.half * 2.5) add(s.x + s.dx * d, s.y + s.dy * d, s.half + 0.5);
  }

  // ───────────────────────── phases ─────────────────────────

  /** A phase change begins (the staged beat): what the old phase left is called off. */
  onPhaseBeat(e: Entity): void {
    this.perch = null;
    this.stance = null;
    this.glyph = null;
    this.downT = 0;
    this.removeEggs();
    this.removeRods();
    this.shell = null;
    this.cracked = false;
    this.setStorm(false);
    this.aloft = false;
    void e;
  }

  /** The beat is over and phase `p` begins. */
  onPhaseStart(e: Entity, p: 2 | 3): void {
    const id = this.c.def.id;
    if (id === 'wb-ape' && p === 2) this.startPerch(e);
    if (id === 'wb-firebird') this.aloft = p === 2;
    if (id === 'wb-firebird' && p === 3) this.eggGap = 0;
    if (id === 'wb-thunder-turtle' && p === 2) this.startShell();
    if (id === 'wb-thunder-turtle' && p === 3) this.setStorm(true);
  }

  // ───────────────────────── the ape ─────────────────────────

  private startPerch(e: Entity): void {
    const s = this.site('wb-ape:ledge');
    e.cast = null;
    Object.assign(e, { x: s.x, y: s.y });
    this.perch = { t: 0, volleyT: 1.5, simT: WB_PERCH.simEvery, breaks: 0, by: new Set() };
    this.c.h.notice(this.c.def.beats[0]);
  }

  private drop(e: Entity, fell: boolean): void {
    const p = this.perch;
    if (!p) return;
    const s = this.site('wb-ape:foot');
    Object.assign(e, { x: s.x, y: s.y });
    e.cast = null;
    this.perch = null;
    if (fell) {
      e.stunT = Math.max(e.stunT, WB_PERCH.stun);
      for (const id of p.by) this.c.credit(id);
      this.c.h.emit({ type: 'dungeonCancel', id: e.id, move: WB_PERCH.volleyMove });
      this.c.h.notice(`${this.c.def.name}ตกลงมาจากผา!`);
    } else this.c.h.notice(`${this.c.def.name}กระโจนลงมาเอง`);
  }

  private callYoung(e: Entity): void {
    this.adds = this.adds.filter((id) => !this.w.entities.get(id)?.dead && this.w.entities.has(id));
    const n = Math.min(WB_YOUNG.count, WB_YOUNG.max - this.adds.length);
    for (let i = 0; i < n; i++) {
      const a = (i / Math.max(1, n)) * Math.PI * 2;
      const m = this.c.h.spawnMonster(WB_YOUNG.id, e.x + Math.cos(a) * WB_YOUNG.ring, e.y + Math.sin(a) * WB_YOUNG.ring);
      this.adds.push(m.id);
    }
  }

  // ───────────────────────── the bird ─────────────────────────

  private layEggs(): void {
    for (const id of sitesOf('wb-bird:nest').slice(0, WB_EGG.count)) {
      const s = this.site(id);
      const m = this.c.h.spawnMonster(WB_EGG.id, s.x, s.y);
      this.eggs.push({ id: m.id, x: s.x, y: s.y, age: 0, simmed: false });
    }
  }

  private removeEggs(): void {
    for (const g of this.eggs) this.w.entities.delete(g.id);
    this.eggs = [];
  }

  // ───────────────────────── the turtle ─────────────────────────

  private startShell(): void {
    const rods = sitesOf('wb-turtle:rod')
      .slice(0, WB_SHELL.rods)
      .map((id) => {
        const s = this.site(id);
        return this.c.h.spawnMonster(WB_SHELL.rodId, s.x, s.y).id;
      });
    this.shell = { t: WB_SHELL.seconds, rods, simT: WB_SHELL.simEvery };
    this.c.h.notice(this.c.def.beats[0]);
  }

  private rodAlive(id: number): Entity | null {
    const r = this.w.entities.get(id);
    return r && !r.dead && r.hp > 0 ? r : null;
  }

  private removeRods(): void {
    if (this.shell) for (const id of this.shell.rods) this.w.entities.delete(id);
  }

  /** A bolt aimed at a spot near a standing rod strikes the rod instead; true when it did. */
  drawBolt(move: string, shape: BossShape): boolean {
    if (!WB_MOVE_MECH[move]?.rodDraw || !this.shell || shape.k !== 'ring') return false;
    for (const id of this.shell.rods) {
      const r = this.rodAlive(id);
      if (r && Math.hypot(r.x - shape.x, r.y - shape.y) <= WB_SHELL.draw) {
        r.hp -= WB_SHELL.bolt;
        return true;
      }
    }
    return false;
  }

  /** The storm: the zone's weather is forced to a storm for the phase and turns back to grey skies after it. */
  private setStorm(on: boolean): void {
    const wx = this.w.weather;
    if (on && !this.storm) {
      this.weatherWas = wx.kind;
      Object.assign(wx, { from: wx.kind, kind: 'storm', blend: 0, left: 1e5 });
    } else if (!on && this.storm) {
      Object.assign(wx, { from: 'storm', kind: this.weatherWas === 'storm' ? 'cloudy' : (this.weatherWas ?? 'cloudy'), blend: 0, left: 60 });
      this.weatherWas = null;
    }
    this.storm = on;
  }

  /** A glider in a thermal column rises toward the lift until the column's top (the bird, phase 2). */
  private thermals(dt: number, e: Entity): void {
    const p = this.w.player;
    if (!this.aloft || p.dead || !this.w.qinggong.gliding) return;
    const floor = floorAt(this.w.map, p.x, p.y);
    const alt = (floor + p.z) * PX_METRES;
    const top = floorAt(this.w.map, e.x, e.y) * PX_METRES + WB_THERMAL.top;
    if (alt >= top || !this.onSite(p, ['wb-bird:thermal'])) return;
    const lift = (WB_THERMAL.lift * Math.min(1, (top - alt) / 6)) / PX_METRES;
    p.vz += (lift - p.vz) * Math.min(1, WB_THERMAL.grip * dt);
  }

  // ───────────────────────── hits on the boss ─────────────────────────

  /** Any hit in the fight: remembers who last hit an egg or a rod. */
  onAnyHit(srcId: number, tgtId: number): void {
    if (this.w.entities.get(srcId)?.kind === 'player') this.lastHit.set(tgtId, srcId);
  }

  /** A hit on the boss by `src`: the share of its damage that counts (0 to 1.25), and the stance's bounce. */
  onBossHit(e: Entity, src: Entity | undefined, skillId: string, outcome: string, damage: number): number {
    const landed = outcome === 'hit' || outcome === 'guardBreak' || outcome === 'interrupt';
    if (!landed || !src) return 1;
    const sk = getSkill(skillId);
    const cat: 'attack' | 'break' = sk.kind === 'break' ? 'break' : 'attack';
    if (src.kind === 'player') this.hist.push({ t: this.clock, kind: cat });
    if (this.hist.length > 60) this.hist.shift();
    let keep = 1;
    if (this.stance && cat === this.stance.counters) {
      keep = 0;
      if (this.stance.kind === 'guard' && src.kind === 'player' && !src.dead)
        this.c.h.hit(e, src, WB_MIMIC.bounce, 'attack', WB_MIMIC.move);
    }
    if (this.aloft && this.downT <= 0 && src.kind === 'player') {
      const floorM = floorAt(this.w.map, e.x, e.y) * PX_METRES;
      const high = (floorAt(this.w.map, src.x, src.y) + src.z) * PX_METRES - floorM >= WB_AIR.reach;
      if (!high) keep *= sk.range >= WB_AIR.ranged ? WB_AIR.rangedMul : 0;
    }
    if (this.shell) keep *= WB_SHELL.taken;
    else if (this.cracked) keep *= WB_SHELL.crackTaken;
    // A break that lands on the ape's ledge counts toward bringing it down.
    if (this.perch && cat === 'break' && src.kind === 'player' && damage >= 0) {
      this.perch.breaks++;
      this.perch.by.add(src.id);
      if (this.perch.breaks >= WB_PERCH.breaks) this.drop(e, true);
    }
    if (damage > 0 && keep !== 1) {
      if (keep < 1) e.hp = Math.min(e.maxHp, e.hp + damage * (1 - keep));
      else e.hp = Math.max(1, e.hp - damage * (keep - 1));
    }
    return keep;
  }

  // ───────────────────────── the tick ─────────────────────────

  step(dt: number, e: Entity): void {
    const w = this.w;
    this.clock += dt;
    // Ground patches.
    for (const p of this.patches) {
      p.t -= dt;
      p.acc += dt;
      if (p.acc < WB_PATCH.every) continue;
      p.acc -= WB_PATCH.every;
      for (const v of w.entities.values()) {
        if (v.dead || !w.isHostile(e, v) || v.z > MOVEMENT.airDodgeHeight) continue;
        if (Math.hypot(v.x - p.x, v.y - p.y) > p.r) continue;
        if (p.kind === 'fire') this.c.h.hit(e, v, WB_PATCH.fire, 'power', p.move);
        else w.addStatus(v, WB_PATCH.smokeStatus, 1, e.id);
      }
    }
    this.patches = this.patches.filter((p) => p.t > 0);
    for (const c of this.covers) c.t -= dt;
    this.covers = this.covers.filter((c) => c.t > 0);
    this.hist = this.hist.filter((x) => this.clock - x.t <= WB_MIMIC.window * 2);
    if (this.stance && (this.stance.t -= dt) <= 0) this.stance = null;
    // Deferred bands strike.
    if (this.pending.length) {
      const due: Pending[] = [];
      for (const p of this.pending) if ((p.t -= dt) <= 0) due.push(p);
      if (due.length) {
        this.pending = this.pending.filter((p) => !due.includes(p));
        for (const p of due) this.c.strike(e, p.move, [p.tele]);
      }
    }
    const here = this.c.humanIn();
    // The ape on its ledge.
    const pc = this.perch;
    if (pc) {
      const ledge = this.site('wb-ape:ledge');
      Object.assign(e, { x: ledge.x, y: ledge.y });
      pc.t += dt;
      pc.volleyT -= dt;
      if (pc.volleyT <= 0 && !e.cast && e.stunT <= 0 && here && w.useSkill(e, WB_PERCH.volleyMove, w.player)) pc.volleyT = WB_PERCH.every;
      if (this.c.mode === 'npc' && here && (pc.simT -= dt) <= 0) {
        pc.simT = WB_PERCH.simEvery;
        if (this.c.h.random() < WB_SIM.mechanic && ++pc.breaks >= WB_PERCH.breaks) this.drop(e, true);
      }
      if (this.perch && pc.t >= WB_PERCH.max) this.drop(e, false);
    }
    // The bird lying in the ash takes to the air again.
    if (this.downT > 0 && (this.downT -= dt) <= 0) this.aloft = this.c.phase() === 2;
    this.thermals(dt, e);
    this.stepEggs(dt, e, here);
    this.stepShell(dt, e, here);
    this.adds = this.adds.filter((id) => {
      const a = w.entities.get(id);
      return !!a && !a.dead;
    });
  }

  private destroyed(id: number): boolean {
    const g = this.w.entities.get(id);
    return !g || g.dead || g.hp <= 0;
  }

  private stepEggs(dt: number, e: Entity, here: boolean): void {
    const w = this.w;
    const keep: Egg[] = [];
    for (const g of this.eggs) {
      g.age += dt;
      if (this.destroyed(g.id)) {
        const by = this.lastHit.get(g.id);
        if (by != null) this.c.credit(by);
        w.entities.delete(g.id);
        continue;
      }
      if (this.c.mode === 'npc' && here && !g.simmed && g.age >= WB_EGG.simAt) {
        g.simmed = true;
        if (this.c.h.random() < WB_EGG.simBreak) {
          w.entities.delete(g.id);
          continue;
        }
      }
      if (g.age >= WB_EGG.fuse) {
        w.entities.delete(g.id);
        e.hp = Math.min(e.maxHp, e.hp + e.maxHp * WB_EGG.heal);
        this.c.h.notice('ไข่เพลิงฟักตัว! นกได้พลังคืน');
        continue;
      }
      keep.push(g);
    }
    const had = this.eggs.length > 0;
    this.eggs = keep;
    if (had && !keep.length) this.eggGap = WB_EGG.again;
    if (this.c.def.id !== 'wb-firebird' || this.c.phase() !== 3) return;
    if (!keep.length) {
      this.eggGap -= dt;
      if (this.eggGap <= 0 && !e.cast && e.stunT <= 0 && here) w.useSkill(e, WB_EGG.layMove, w.player);
    }
  }

  private stepShell(dt: number, e: Entity, here: boolean): void {
    const sh = this.shell;
    if (!sh) return;
    const w = this.w;
    sh.t -= dt;
    if (this.c.mode === 'npc' && here && (sh.simT -= dt) <= 0) {
      sh.simT = WB_SHELL.simEvery;
      const alive = sh.rods.filter((id) => this.rodAlive(id));
      if (alive.length && this.c.h.random() < WB_SIM.mechanic) w.entities.delete(alive[0]!);
    }
    const alive = sh.rods.filter((id) => !this.destroyed(id) && w.entities.has(id));
    for (const id of sh.rods) {
      if (alive.includes(id)) continue;
      const by = this.lastHit.get(id);
      if (by != null) this.c.credit(by);
      this.lastHit.delete(id);
      w.entities.delete(id);
    }
    sh.rods = alive;
    if (!alive.length) {
      this.shell = null;
      this.cracked = true;
      e.cast = null;
      e.stunT = Math.max(e.stunT, WB_SHELL.crackStun);
      this.c.h.notice(`กระดอง${this.c.def.name}แตก!`);
    } else if (sh.t <= 0) {
      this.removeRods();
      this.shell = null;
      this.c.h.notice(`${this.c.def.name}เผยหัวออกมา`);
    }
  }

  // ───────────────────────── the end ─────────────────────────

  /** The fight is over: every add, egg and rod goes. */
  clear(): TeleEndIds {
    const ids = this.onCancel();
    for (const id of this.adds) this.w.entities.delete(id);
    this.adds = [];
    this.removeEggs();
    this.removeRods();
    this.shell = null;
    this.setStorm(false);
    this.patches = [];
    this.covers = [];
    this.perch = null;
    this.stance = null;
    return ids;
  }

  // ───────────────────────── read side ─────────────────────────

  view(): MechView {
    const p = this.perch;
    const ledge = p ? this.site('wb-ape:ledge') : null;
    return {
      perch: p && ledge ? { x: ledge.x, y: ledge.y, z: ledge.z, breaks: p.breaks, need: WB_PERCH.breaks, left: Math.max(0, WB_PERCH.max - p.t) } : null,
      mimicGlyph: this.glyph,
      stance: this.stance ? { kind: this.stance.kind, counters: this.stance.counters, left: this.stance.t } : null,
      aloft: this.aloft && this.downT <= 0,
      altitude: this.aloft && this.downT <= 0 ? WB_AIR.alt : 0,
      downFor: Math.max(0, this.downT),
      eggs: this.eggs.map((g) => ({ id: g.id, x: g.x, y: g.y, left: Math.max(0, WB_EGG.fuse - g.age) })),
      shell: this.shell ? { left: Math.max(0, this.shell.t), rods: this.shell.rods.length, total: WB_SHELL.rods } : null,
      cracked: this.cracked,
      storm: this.storm,
      swell: this.storm ? WB_STORM.swell : 1,
      marks: this.marks,
      thermals: this.aloft ? sitesOf('wb-bird:thermal').map((id) => this.site(id)) : [],
      patches: this.patches.map((q) => ({ kind: q.kind, x: q.x, y: q.y, r: q.r, left: Math.max(0, q.t) })),
      covers: this.covers.map((q) => ({ x: q.x, y: q.y, r: WB_COVER.r, left: Math.max(0, q.t) })),
      adds: this.adds.length,
    };
  }

  /** Whether an entity is one of the inert adds (egg, rod): the World's own monster thinking leaves it alone. */
  static inert(defId: string | undefined): boolean {
    return !!defId && WB_INERT.has(defId);
  }
}

type TeleEndIds = number[];
